import type { UserRole } from './types';

// ─── Permission Definitions ────────────────────────────────────

export type Permission =
  // Dashboard
  | 'dashboard.view'
  // Emergency center (ARC monitoring)
  | 'emergency.view'
  | 'emergency.assign_patrol'
  | 'emergency.update_status'
  // Tickets / history
  | 'tickets.view'
  | 'tickets.create'
  | 'tickets.update'
  | 'tickets.delete'
  // Users
  | 'users.view'
  | 'users.create'
  | 'users.update'
  | 'users.delete'
  | 'users.manage_roles'
  // Accounts (T-013)
  | 'accounts.view'
  | 'accounts.manage'
  // Cameras
  | 'cameras.view'
  | 'cameras.manage'
  // Alarm panels
  | 'alarms.view'
  | 'alarms.manage'
  // ARC sub-modules
  | 'arc.events.view'
  | 'arc.contacts.view'
  | 'arc.contacts.manage'
  | 'arc.cid_codes.view'
  | 'arc.cid_codes.manage'
  // Geofences
  | 'geofences.view'
  | 'geofences.manage'
  // Subscription plans (F5)
  | 'plans.view'
  | 'plans.manage'
  // Revenue / billing analytics (F5)
  | 'revenue.view'
  // Communications / broadcasts (F6)
  | 'communications.view'
  | 'communications.send'
  // Collections / payments (F9)
  | 'collections.view'
  | 'collections.confirm'
  // Billing operations (invoicing, contracts, products, FEL config)
  | 'billing.view'
  | 'billing.manage'
  // System
  | 'changelog.view'
  | 'settings.manage'
  | 'audit.view';

// ─── Role → Permissions Map ────────────────────────────────────

const OPERATOR_PERMISSIONS: Permission[] = [
  'dashboard.view',
  'emergency.view',
  'emergency.assign_patrol',
  'emergency.update_status',
  'tickets.view',
  'tickets.update',
  'cameras.view',
  'alarms.view',
  // Operators can edit site/panel details (basic info, verification word,
  // zones, contacts) — day-to-day account maintenance tasks. Matches the
  // 'operator' row in the `roles` table (single source of truth in prod;
  // this static array is only the fallback used if that query fails).
  'alarms.manage',
  'arc.events.view',
  'arc.contacts.view',
  'arc.contacts.manage',
  'arc.cid_codes.view',
  // 2026-10-29 roles matrix: users, settings and account editing moved to admins / sellers.
  // Accounts stay visible (which account a site belongs to).
  'accounts.view',
  'changelog.view',
];

/** Vendedor (2026-10-29): new clients — accounts, sites, invitations, plans and payment status. */
const SELLER_PERMISSIONS: Permission[] = [
  'accounts.view',
  'accounts.manage',
  'plans.view',
  'collections.view',
  'cameras.view',
  'changelog.view',
];

/**
 * Técnico (2026-10-29): installation — panels, zones, cameras, gateways and routers. ARC events only of
 * the sites where he is the installer (enforced by RLS, alarm_sites.installer_id).
 */
const TECHNICIAN_PERMISSIONS: Permission[] = [
  'alarms.view',
  'alarms.manage',
  'cameras.view',
  'cameras.manage',
  'accounts.view',
  'arc.events.view',
  'arc.cid_codes.view',
  'changelog.view',
];

const ADMIN_PERMISSIONS: Permission[] = [
  ...OPERATOR_PERMISSIONS,
  'tickets.create',
  'tickets.delete',
  'users.view',
  'users.create',
  'users.update',
  'users.manage_roles',
  'cameras.manage',
  'arc.contacts.manage',
  'arc.cid_codes.manage',
  'geofences.view',
  'geofences.manage',
  'plans.view',
  'revenue.view',
  'communications.view',
  'communications.send',
  'collections.view',
  'collections.confirm',
  'billing.view',
  'billing.manage',
  'audit.view',
];

const SUPER_ADMIN_PERMISSIONS: Permission[] = [
  ...ADMIN_PERMISSIONS,
  'users.delete',
  'users.manage_roles',
  'plans.view',
  'plans.manage',
  'revenue.view',
  'communications.view',
  'communications.send',
];

export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  client: [],
  patrol: [],
  operator: OPERATOR_PERMISSIONS,
  seller: SELLER_PERMISSIONS,
  technician: TECHNICIAN_PERMISSIONS,
  admin: ADMIN_PERMISSIONS,
  super_admin: SUPER_ADMIN_PERMISSIONS,
};

// ─── Helpers ──────────────────────────────────────────────────

export function roleHasPermission(role: UserRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

export function roleHasAnyPermission(role: UserRole, permissions: Permission[]): boolean {
  return permissions.some((p) => ROLE_PERMISSIONS[role].includes(p));
}
